/**
 * Plain-words description of what changed between two snapshots of an onboarding step's state, for the message
 * the voice agent gets when the user edits something by hand ("The user just filled these by hand: ...").
 *
 * Nested objects (the team step's `draft`) are described field by field and lists of objects by their names, so
 * the agent never receives "[object Object]".
 */

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const describeValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(describeValue).filter(Boolean).join(', ');
  if (isPlainObject(v)) {
    const label = v.name ?? v.label ?? v.email;
    return label ? String(label) : JSON.stringify(v);
  }
  return String(v ?? '');
};

const changesFor = (key: string, cur: unknown, prev: unknown): string[] => {
  if (isPlainObject(cur)) {
    const before = isPlainObject(prev) ? prev : {};
    return Object.keys(cur)
      .filter((sub) => JSON.stringify(cur[sub]) !== JSON.stringify(before[sub]))
      .flatMap((sub) => changesFor(`${key}.${sub}`, cur[sub], before[sub]));
  }
  const text = describeValue(cur);
  return [text ? `${key} = "${text}"` : `${key} (cleared)`];
};

// State the step reports for the agent's benefit, not things the user did: bookkeeping that changes when a request
// finishes or a list finishes loading. Diffing them would tell the agent "the user just filled these by hand".
const NOT_USER_EDITS = ['lastActionResult', 'invitationsLoading'];

/** The step state with the bookkeeping fields removed, for the watcher that reports hand edits. */
export function watchableState(state: Record<string, unknown> | null | undefined): Record<string, unknown> | null {
  if (!state) return null;
  const rest = { ...state };
  for (const key of NOT_USER_EDITS) delete rest[key];
  return rest;
}

/** One entry per changed field, e.g. `draft.email = "a@b.co"`, or `invitations (cleared)`. */
export function describeStepChange(
  current: Record<string, unknown> | null | undefined,
  previous: Record<string, unknown> | null | undefined,
): string[] {
  const cur = current ?? {};
  const prev = previous ?? {};
  return Object.keys(cur)
    .filter((k) => JSON.stringify(cur[k]) !== JSON.stringify(prev[k]))
    .flatMap((k) => changesFor(k, cur[k], prev[k]));
}
